import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Badge,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  Stack,
  Chip,
  Tooltip,
  Button,
  Alert,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  getDocs,
  doc,
  getDoc,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import type { MessageChannel, ChannelMessage, Conversation } from '../types/messaging';
import {
  normalizeContactId,
  normalizePhoneDigits,
  formatBrokerDisplayName,
  formatMessageTimestamp,
  formatPhoneDisplay,
  formatUnsupportedMessageLabel,
  getMessageDisplayText,
  getTemplateDisplayName,
} from '../utils/messaging';
import ChannelChat from '../components/ChannelChat';
import AddLeadDrawer from '../components/AddLeadDrawer';
import LeadDetailDrawer from '../components/LeadDetailDrawer';

const GOLD = '#9d6f32';
const DARK = '#1a1a1a';
const MUTED = '#6b6b6b';
const BORDER = '#e8e8e8';
const SIDEBAR_BG = '#fafaf9';
const FONT_BODY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const FONT_HEADING = "'Georgia', 'Times New Roman', serif";
const CUSTOMER_BROKER_LOOKBACK_DAYS = 30;
const CUSTOMER_BROKER_LOOKBACK_MS = CUSTOMER_BROKER_LOOKBACK_DAYS * 24 * 60 * 60 * 1000;
const WHATSAPP_MESSAGES_PAGE_SIZE = 500;

type LeadRow = {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  source?: string;
  status: string;
  [key: string]: unknown;
};

type LeadMapEntry = {
  id: string;
  name: string;
  assignedTo: string | null;
  status: string | null;
};

type BrokerMapEntry = {
  id: string;
  name: string;
  agencyName: string | null;
};

type CustomerMapEntry = {
  projectId: string;
  projectName: string;
  unitId: string;
  unitNumber: string;
  fullName: string;
  linkedUnits: Array<{ projectId: string; projectName: string; unitId: string; unitNumber: string }>;
};

function addCustomerMatch(
  map: Map<string, CustomerMapEntry>,
  phoneDigits: string,
  entry: Omit<CustomerMapEntry, 'linkedUnits'>,
  preferName = false
) {
  if (phoneDigits.length < 10 || !entry.fullName) return;
  const phone10 = phoneDigits.slice(-10);
  const linkedUnit = {
    projectId: entry.projectId,
    projectName: entry.projectName,
    unitId: entry.unitId,
    unitNumber: entry.unitNumber,
  };
  const existing = map.get(phone10);
  if (!existing) {
    map.set(phone10, { ...entry, linkedUnits: [linkedUnit] });
    return;
  }
  const linkedUnits = existing.linkedUnits.some((unit) =>
    unit.projectId === entry.projectId && unit.unitId === entry.unitId
  ) ? existing.linkedUnits : [...existing.linkedUnits, linkedUnit];
  map.set(phone10, {
    ...(preferName && existing.projectId === entry.projectId && existing.unitId === entry.unitId
      ? entry : existing),
    linkedUnits,
  });
}

type ConversationLeadFilter =
  | 'all'
  | 'my_leads'
  | 'customer'
  | 'broker'
  | 'unlinked'
  | 'new_status'
  | 'unread'
  | 'not_lead';

const AVATAR_COLORS = ['#E6F1FB', '#EAF3DE', '#FAEEDA', '#FAECE7', '#EEEDFE', '#E1F5EE', '#FBEAF0'];
const AVATAR_TEXT = ['#0C447C', '#27500A', '#633806', '#712B13', '#3C3489', '#085041', '#72243E'];

function hashCode(s: string) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function getInitials(name: string | null): string {
  if (!name || name === '--') return '?';
  const words = name.split(' ').filter((w) => w.length > 0);
  if (!words.length) return '?';
  return words
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function buildCustomerDisplayName(row: Record<string, unknown>): string {
  const direct = String(row.displayName || row.name || '').trim();
  if (direct) return direct;
  return [
    String(row.firstName || '').trim(),
    String(row.middleName || '').trim(),
    String(row.surname || row.lastName || '').trim(),
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
}

function extractCustomerPhoneDigits(row: Record<string, unknown>): string {
  return normalizePhoneDigits(
    String(
      row.primaryCustomerMobile ||
      row.customerMobile ||
      row.mobile ||
      row.mobileNo ||
      row.phone ||
      row.phoneNumber ||
      ''
    ).trim()
  );
}

function messageCreatedAtMs(msg: Pick<ChannelMessage, 'createdAt'>): number {
  const value = msg.createdAt;
  if (!value) return 0;
  if (typeof (value as { toMillis?: () => number }).toMillis === 'function') {
    return (value as { toMillis: () => number }).toMillis();
  }
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate().getTime();
  }
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value as unknown as string | number);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

const channelIcon = (ch: MessageChannel, size = 16) => {
  const sx = { fontSize: size };
  if (ch === 'whatsapp') return <WhatsAppIcon sx={{ ...sx, color: '#25d366' }} />;
  if (ch === 'instagram') return <InstagramIcon sx={{ ...sx, color: '#E1306C' }} />;
  if (ch === 'facebook') return <FacebookIcon sx={{ ...sx, color: '#1877F2' }} />;
  return null;
};

export default function MessagingInboxPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentUser, roleId } = useAuth();
  const isAdmin = String(roleId || '').toLowerCase() === 'admin' || String(roleId || '').toLowerCase() === 'super-admin';
  // Admins and sales managers see every chat; the sales team sees only the
  // chats of leads assigned to them (whole history), listed from chat cards.
  const seeAllChats = isAdmin || String(roleId || '').toLowerCase() === 'sales-manager';
  const ownEmail = String(currentUser?.email || '').trim().toLowerCase();
  const [cardUnread, setCardUnread] = useState<Map<string, number>>(new Map());
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [latestMessages, setLatestMessages] = useState<ChannelMessage[]>([]);
  const [templateEdits, setTemplateEdits] = useState<Record<string, any>>({});
  const [olderMessages, setOlderMessages] = useState<ChannelMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [oldestLoadedDoc, setOldestLoadedDoc] = useState<any | null>(null);
  const [search, setSearch] = useState('');
  const [selectedConv, setSelectedConv] = useState<Conversation | null>(null);
  const [channelFilter, setChannelFilter] = useState<MessageChannel | 'all'>('all');
  const [leadFilter, setLeadFilter] = useState<ConversationLeadFilter>('my_leads');
  const [leadMap, setLeadMap] = useState<Map<string, LeadMapEntry>>(new Map());
  const [brokerMap, setBrokerMap] = useState<Map<string, BrokerMapEntry>>(new Map());
  const [customerMapByProject, setCustomerMapByProject] = useState<Map<string, Map<string, CustomerMapEntry>>>(new Map());
  const [contactNames, setContactNames] = useState<Map<string, string>>(new Map());
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [addLeadPrefill, setAddLeadPrefill] = useState<{ phone: string; name: string; source: string } | null>(null);
  const [drawerLead, setDrawerLead] = useState<LeadRow | null>(null);
  const [leadDrawerOpen, setLeadDrawerOpen] = useState(false);
  useEffect(() => onSnapshot(collection(db, 'whatsappTemplateEdits'), (snap) => {
    const edits: Record<string, any> = {};
    snap.docs.forEach((row) => { edits[row.id] = row.data(); });
    setTemplateEdits(edits);
  }), []);

  // Sales team: one row per own chat card, shaped like its latest message.
  useEffect(() => {
    if (seeAllChats) return;
    if (!ownEmail) return;
    setHasMoreMessages(false);
    return onSnapshot(
      query(collection(db, 'whatsappChats'), where('assignedTo', '==', ownEmail), orderBy('lastMessageAt', 'desc'), limit(500)),
      (snap) => {
        const unread = new Map<string, number>();
        setLatestMessages(snap.docs.map((d) => {
          const c = d.data() as any;
          unread.set(d.id, Math.max(0, Number(c.unreadCount || 0)));
          return {
            id: `card-${d.id}`,
            channel: (c.channel as MessageChannel) || 'whatsapp',
            contactPhone: d.id,
            direction: c.lastDirection === 'inbound' ? 'inbound' : 'outbound',
            type: 'text',
            text: String(c.lastPreview || ''),
            fromName: c.fromName || c.leadName || null,
            createdAt: c.lastMessageAt || null,
            readAt: c.lastMessageAt || null,
          } as unknown as ChannelMessage;
        }));
        setCardUnread(unread);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [seeAllChats, ownEmail]);

  useEffect(() => {
    if (!seeAllChats) return;
    const q = query(
      collection(db, 'whatsappMessages'),
      orderBy('createdAt', 'desc'),
      limit(WHATSAPP_MESSAGES_PAGE_SIZE)
    );
    const unsub = onSnapshot(q, (snap) => {
      const msgs: ChannelMessage[] = snap.docs.map((d) => ({
        id: d.id,
        channel: (d.data().channel as MessageChannel) || 'whatsapp',
        ...d.data(),
      } as ChannelMessage));
      setLatestMessages(msgs);
      setHasMoreMessages(snap.docs.length === WHATSAPP_MESSAGES_PAGE_SIZE);
      setOldestLoadedDoc((prev: any) => prev || snap.docs[snap.docs.length - 1] || null);
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [seeAllChats]);

  const loadOlderMessages = useCallback(async () => {
    if (!hasMoreMessages || loadingMoreMessages || !oldestLoadedDoc) return;
    setLoadingMoreMessages(true);
    try {
      const q = query(
        collection(db, 'whatsappMessages'),
        orderBy('createdAt', 'desc'),
        startAfter(oldestLoadedDoc),
        limit(WHATSAPP_MESSAGES_PAGE_SIZE)
      );
      const snap = await getDocs(q);
      const msgs: ChannelMessage[] = snap.docs.map((d) => ({
        id: d.id,
        channel: (d.data().channel as MessageChannel) || 'whatsapp',
        ...d.data(),
      } as ChannelMessage));
      if (msgs.length > 0) {
        setOlderMessages((prev) => {
          const seen = new Set(prev.map((msg) => msg.id));
          const next = [...prev];
          msgs.forEach((msg) => {
            if (!seen.has(msg.id)) next.push(msg);
          });
          return next;
        });
        setOldestLoadedDoc(snap.docs[snap.docs.length - 1] || oldestLoadedDoc);
      }
      setHasMoreMessages(snap.docs.length === WHATSAPP_MESSAGES_PAGE_SIZE);
    } catch (err) {
      console.error('Failed to load older whatsapp messages', err);
    } finally {
      setLoadingMoreMessages(false);
    }
  }, [hasMoreMessages, loadingMoreMessages, oldestLoadedDoc]);

  // Numbers marked "Not a lead" (sales pitch, supplier, job seeker, other).
  const [notLeadPhones, setNotLeadPhones] = useState<Map<string, string>>(new Map());
  useEffect(
    () =>
      onSnapshot(
        query(collection(db, 'whatsappContactState'), where('optOutReason', '==', 'not-a-lead')),
        (snap) => setNotLeadPhones(new Map(snap.docs.map((d) => [d.id, String((d.data() as any).notLeadType || 'other')]))),
        () => setNotLeadPhones(new Map())
      ),
    []
  );

  // Open WhatsApp requests (visit booked / call back) by phone10, until someone replies.
  const [openRequests, setOpenRequests] = useState<Map<string, { type: string; label: string }>>(new Map());
  useEffect(() => {
    const since = Timestamp.fromMillis(Date.now() - 14 * 86400000);
    return onSnapshot(
      query(collection(db, 'whatsappContactState'), where('openRequestAt', '>=', since)),
      (snap) => {
        const m = new Map<string, { type: string; label: string }>();
        snap.docs.forEach((d) => {
          const r = (d.data() as any).openRequest;
          if (r?.label) m.set(d.id, { type: String(r.type || ''), label: String(r.label) });
        });
        setOpenRequests(m);
      },
      () => setOpenRequests(new Map())
    );
  }, []);

  useEffect(() => {
    getDocs(query(collection(db, 'leads'), orderBy('createdAt', 'desc'), limit(2000))).then((snap) => {
      const m = new Map<string, LeadMapEntry>();
      snap.docs.forEach((d) => {
        const data = d.data();
        const phone = String(data.phone || '').replace(/\D/g, '');
        const phone10 = String(data.phone10 || '').trim();
        const name = String(data.name || '').trim();
        const entry: LeadMapEntry = {
          id: d.id,
          name,
          assignedTo: String(data.assignedTo || '').trim() || null,
          status: String(data.status || '').trim() || null,
        };
        if (phone10) m.set(phone10, entry);
        if (phone.length >= 10) m.set(phone.slice(-10), entry);
      });
      setLeadMap(m);
    });
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'brokers'), limit(500)), (snap) => {
      const m = new Map<string, BrokerMapEntry>();
      snap.docs.forEach((d) => {
        const data = d.data() as Record<string, unknown>;
        const name = String(data.brokerName || data.displayName || '').trim();
        const agencyName = String(data.agencyName || '').trim() || null;
        const phoneDigits = normalizePhoneDigits(
          String(data.phoneE164 || '').trim() ||
          `${String(data.phoneCountryCode || '').trim()}${String(data.phoneNational || '').trim()}`
        );
        const phone10 = phoneDigits.length >= 10 ? phoneDigits.slice(-10) : '';
        const entry = { id: d.id, name, agencyName };
        if (phone10) m.set(phone10, entry);
      });
      setBrokerMap(m);
    }, () => setBrokerMap(new Map()));
    return unsub;
  }, []);

  useEffect(() => {
    let cancelled = false;
    let refreshVersion = 0;

    const loadProjectCustomerMap = async (projectId: string, projectName: string, version: number) => {
      try {
        const unitsSnap = await getDocs(collection(db, 'projects', projectId, 'units'));
        if (cancelled || version !== refreshVersion) return;

        const projectMap = new Map<string, CustomerMapEntry>();
        const targets = unitsSnap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          const unitId = d.id;
          const unitNumber = String(
            data.unitNumber ||
            data.unitNo ||
            data.unit ||
            unitId
          ).trim();
          const directName = buildCustomerDisplayName(data);
          const directPhone = extractCustomerPhoneDigits(data);
          const bookingId = String(data.activeBookingId || data.bookingId || '').trim();
          return { unitId, unitNumber, directName, directPhone, bookingId };
        });

        targets.forEach((row) => {
          if (row.directPhone && row.directName) {
            addCustomerMatch(projectMap, row.directPhone, {
              projectId,
              projectName,
              unitId: row.unitId,
              unitNumber: row.unitNumber,
              fullName: row.directName,
            });
          }
        });

        const bookingTargets = targets;
        await Promise.all(
          bookingTargets.map(async (row) => {
            try {
              let bookingId = String(row.bookingId || '').trim();
              if (!bookingId) {
                const bookingsSnap = await getDocs(
                  query(collection(db, 'projects', projectId, 'units', row.unitId, 'bookings'), limit(20))
                );
                const bookingRows: Array<Record<string, unknown>> = bookingsSnap.docs.map((d) => ({ id: d.id, ...(d.data() as Record<string, unknown>) }));
                const toMillis = (value: unknown) =>
                  typeof (value as { toMillis?: () => number } | null)?.toMillis === 'function'
                    ? (value as { toMillis: () => number }).toMillis()
                    : typeof (value as { seconds?: number } | null)?.seconds === 'number'
                    ? Number((value as { seconds: number }).seconds) * 1000
                    : 0;
                const pickBooking = (rows: Array<Record<string, unknown>>) =>
                  rows
                    .slice()
                    .sort((a, b) => {
                      const updatedA = toMillis(a?.updatedAt || a?.createdAt);
                      const updatedB = toMillis(b?.updatedAt || b?.createdAt);
                      if (updatedA !== updatedB) return updatedB - updatedA;
                      return String(b?.id || '').localeCompare(String(a?.id || ''));
                    })[0] || null;
                const activeBookingRow =
                  bookingRows.find((bookingRow) => bookingRow?.isActive === true) ||
                  bookingRows.find((bookingRow) => String(bookingRow?.status || '').trim().toLowerCase() === 'active') ||
                  pickBooking(bookingRows);
                bookingId = String(activeBookingRow?.id || '').trim();
              }
              if (!bookingId) return;

              const customersSnap = await getDocs(
                collection(db, 'projects', projectId, 'units', row.unitId, 'bookings', bookingId, 'customers')
              );
              if (cancelled || version !== refreshVersion || customersSnap.empty) return;

              customersSnap.docs.forEach((customerDoc) => {
                const customer = customerDoc.data() as Record<string, unknown>;
                const name = buildCustomerDisplayName(customer);
                const phoneDigits = extractCustomerPhoneDigits(customer);
                if (!name || !phoneDigits) return;
                addCustomerMatch(projectMap, phoneDigits, {
                  projectId,
                  projectName,
                  unitId: row.unitId,
                  unitNumber: row.unitNumber,
                  fullName: name,
                }, true);
              });
            } catch (err) {
              console.error('Failed to load project customer lookup', err);
            }
          })
        );

        if (cancelled || version !== refreshVersion) return;
        setCustomerMapByProject((prev) => {
          const next = new Map(prev);
          next.set(projectId, projectMap);
          return next;
        });
      } catch (err) {
        console.error('Failed to load project customer map', err);
      }
    };

    const run = async () => {
      const version = ++refreshVersion;
      try {
        const projectsSnap = await getDocs(collection(db, 'projects'));
        if (cancelled || version !== refreshVersion) return;
        projectsSnap.docs.forEach((projectDoc) => {
          const data = projectDoc.data() as Record<string, unknown>;
          const projectId = projectDoc.id;
          const projectName = String(data.name || data.title || projectId).trim();
          void loadProjectCustomerMap(projectId, projectName, version);
        });
      } catch (err) {
        console.error('Failed to initialize project customer map', err);
      }
    };

    void run();
    const refreshOnFocus = () => { void run(); };
    window.addEventListener('focus', refreshOnFocus);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'whatsappContacts'), limit(1000)), (snap) => {
      const m = new Map<string, string>();
      snap.docs.forEach((d) => {
        const data = d.data();
        const phone = String(data.phone || d.id).replace(/\D/g, '');
        const name = String(data.confirmedName || '').trim();
        if (phone && name) m.set(phone.slice(-10), name);
      });
      setContactNames(m);
    }, () => {});
    return unsub;
  }, []);

  const customerMap = useMemo(() => {
    const merged = new Map<string, CustomerMapEntry>();
    customerMapByProject.forEach((projectMap) => {
      projectMap.forEach((entry, phone10) => {
        addCustomerMatch(merged, phone10, entry);
        entry.linkedUnits.slice(1).forEach((unit) => {
          addCustomerMatch(merged, phone10, { ...unit, fullName: entry.fullName });
        });
      });
    });
    return merged;
  }, [customerMapByProject]);

  const allMessages = useMemo(() => {
    const merged = new Map<string, ChannelMessage>();
    [...latestMessages, ...olderMessages].forEach((msg) => {
      if (!msg?.id) return;
      merged.set(msg.id, msg);
    });
    return Array.from(merged.values()).sort((a, b) => messageCreatedAtMs(b) - messageCreatedAtMs(a));
  }, [latestMessages, olderMessages]);

  // The lead list above holds only the newest 2000 leads. Look up any chat
  // number it missed by phone, so an older lead opens instead of "Add as new lead".
  const [lookedUpPhones] = useState(() => new Set<string>());
  useEffect(() => {
    if (!leadMap.size) return;
    const missing = Array.from(new Set(allMessages.map((m) => String(m.contactPhone || '').replace(/\D/g, '').slice(-10))))
      .filter((p) => p.length === 10 && !leadMap.has(p) && !lookedUpPhones.has(p));
    if (!missing.length) return;
    missing.forEach((p) => lookedUpPhones.add(p));
    const chunks: string[][] = [];
    for (let i = 0; i < missing.length; i += 30) chunks.push(missing.slice(i, i + 30));
    Promise.all(chunks.map((chunk) => getDocs(query(collection(db, 'leads'), where('phone10', 'in', chunk)))))
      .then((snaps) => {
        const found = new Map<string, LeadMapEntry>();
        snaps.forEach((snap) => snap.docs.forEach((d) => {
          const data = d.data();
          found.set(String(data.phone10 || '').trim(), {
            id: d.id,
            name: String(data.name || '').trim(),
            assignedTo: String(data.assignedTo || '').trim() || null,
            status: String(data.status || '').trim() || null,
          });
        }));
        if (found.size) setLeadMap((prev) => new Map([...Array.from(prev.entries()), ...Array.from(found.entries())]));
      })
      .catch((err) => console.error('Failed to look up older leads for chats', err));
  }, [allMessages, leadMap, lookedUpPhones]);

  const conversations = useMemo(() => {
    const map = new Map<string, Conversation>();
    for (const msg of allMessages) {
      const phone = msg.contactPhone;
      if (!phone) continue;
      const ch = msg.channel || 'whatsapp';
      const cid = normalizeContactId(ch, phone);

      if (!map.has(cid)) {
        const phone10 = phone.slice(-10);
        const lead = leadMap.get(phone10);
        const broker = brokerMap.get(phone10);
        const customer = customerMap.get(phone10);
        const brokerDisplay = broker ? formatBrokerDisplayName(broker.name, broker.agencyName) : null;
        const confirmed = contactNames.get(phone10);
        const displayName = customer?.fullName || lead?.name || brokerDisplay || confirmed || msg.fromName || null;

        map.set(cid, {
          contactId: cid,
          channel: ch,
          contactPhone: phone,
          contactName: displayName,
          contactDisplay: displayName || '--',
          lastMessage: msg,
          lastMessageAt: new Date(messageCreatedAtMs(msg) || Date.now()),
          unreadCount: 0,
          leadId: lead?.id || null,
          leadName: lead?.name || null,
          brokerId: broker?.id || null,
          brokerName: broker?.name || null,
          brokerAgencyName: broker?.agencyName || null,
          customerUnitId: customer?.unitId || null,
          customerUnitNumber: customer?.unitNumber || null,
          customerProjectId: customer?.projectId || null,
          customerProjectName: customer?.projectName || null,
          customerName: customer?.fullName || null,
          customerLinkedUnits: customer?.linkedUnits || [],
        });
      }

      const conv = map.get(cid)!;
      if (msg.direction === 'inbound' && !msg.readAt) {
        conv.unreadCount++;
      }
      if (!conv.contactName || conv.contactName === '--') {
        const p10 = phone.slice(-10);
        const confirmed = contactNames.get(p10);
        const broker = brokerMap.get(p10);
        const customer = customerMap.get(p10);
        if (broker) {
          const brokerDisplay = formatBrokerDisplayName(broker.name, broker.agencyName);
          conv.contactName = brokerDisplay || broker.name;
          conv.contactDisplay = conv.contactName;
        } else if (customer) {
          conv.contactName = customer.fullName;
          conv.contactDisplay = customer.fullName;
        } else if (confirmed) {
          conv.contactName = confirmed;
          conv.contactDisplay = confirmed;
        } else if (msg.fromName) {
          conv.contactName = msg.fromName;
          conv.contactDisplay = msg.fromName;
        }
      }
    }
    // Sales team rows come from chat cards, which carry the unread count.
    if (!seeAllChats) {
      map.forEach((conv) => { conv.unreadCount = cardUnread.get(conv.contactPhone || '') || 0; });
    }
    return Array.from(map.values()).sort(
      (a, b) => b.lastMessageAt.getTime() - a.lastMessageAt.getTime()
    );
  }, [allMessages, brokerMap, customerMap, leadMap, contactNames, seeAllChats, cardUnread]);

  const channelScopedConversations = useMemo(() => {
    let list = conversations;
    if (channelFilter !== 'all') {
      list = list.filter((c) => c.channel === channelFilter);
    }
    return list;
  }, [conversations, channelFilter]);

  const myEmail = String(currentUser?.email || '').trim().toLowerCase();
  const customerBrokerCutoffMs = Date.now() - CUSTOMER_BROKER_LOOKBACK_MS;

  const filterConversationByLeadScope = useCallback(
    (conv: Conversation, filterKey: ConversationLeadFilter) => {
      const phone10 = String(conv.contactPhone || '').replace(/\D/g, '').slice(-10);
      const lead = phone10 ? leadMap.get(phone10) : null;
      const assignedTo = String(lead?.assignedTo || '').trim().toLowerCase();
      const status = String(lead?.status || '').trim().toLowerCase();
      const hasLead = !!lead?.id;
      const isRecentCustomerBroker =
        conv.lastMessageAt instanceof Date &&
        conv.lastMessageAt.getTime() >= customerBrokerCutoffMs;

      if (filterKey === 'all') return true;
      // Sales team: every listed chat is already one of their leads.
      if (filterKey === 'my_leads' && !seeAllChats) return true;
      if (filterKey === 'my_leads') {
        return hasLead && !!myEmail && assignedTo === myEmail;
      }
      if (filterKey === 'customer') return !!conv.customerUnitId && isRecentCustomerBroker;
      if (filterKey === 'broker') return !!conv.brokerId && isRecentCustomerBroker;
      if (filterKey === 'not_lead') return notLeadPhones.has(phone10);
      if (filterKey === 'unlinked') return !hasLead && !conv.brokerId && !conv.customerUnitId && !notLeadPhones.has(phone10);
      if (filterKey === 'new_status') return hasLead && status === 'new';
      if (filterKey === 'unread') return conv.unreadCount > 0;
      return true;
    },
    [customerBrokerCutoffMs, leadMap, myEmail, notLeadPhones, seeAllChats]
  );

  const leadFilterCounts = useMemo(() => {
    const keys: ConversationLeadFilter[] = [
      'all',
      'my_leads',
      'customer',
      'broker',
      'unlinked',
      'new_status',
      'unread',
      'not_lead',
    ];
    const counts = Object.fromEntries(
      keys.map((key) => [key, 0])
    ) as Record<ConversationLeadFilter, number>;
    channelScopedConversations.forEach((conv) => {
      keys.forEach((key) => {
        if (filterConversationByLeadScope(conv, key) && conv.unreadCount > 0) {
          counts[key] += 1;
        }
      });
    });
    return counts;
  }, [channelScopedConversations, filterConversationByLeadScope]);

  const filtered = useMemo(() => {
    let list = channelScopedConversations.filter((conv) =>
      filterConversationByLeadScope(conv, leadFilter)
    );
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(
        (c) =>
          (c.contactName?.toLowerCase() || '').includes(s) ||
          (c.contactPhone || '').includes(s) ||
          (c.leadName?.toLowerCase() || '').includes(s)
      );
    }
    return list;
  }, [channelScopedConversations, filterConversationByLeadScope, leadFilter, search]);

  const selectedChatId = useMemo(
    () => String(searchParams.get('chat') || '').trim(),
    [searchParams]
  );

  useEffect(() => {
    if (!isMobile) return;
    if (!selectedChatId) {
      setSelectedConv(null);
      return;
    }
    const matched = conversations.find((conv) => conv.contactId === selectedChatId) || null;
    setSelectedConv(matched);
  }, [conversations, isMobile, selectedChatId]);

  useEffect(() => {
    if (isMobile) return;
    setSelectedConv((previous) => previous
      ? conversations.find((conv) => conv.contactId === previous.contactId) || null
      : null);
  }, [conversations, isMobile]);

  const handleSelect = (conv: Conversation) => {
    if (isMobile) {
      const next = new URLSearchParams(searchParams);
      next.set('chat', conv.contactId);
      setSearchParams(next);
      return;
    }
    setSelectedConv(conv);
  };

  const selectedContactName = useMemo(() => {
    if (!selectedConv?.contactPhone) return selectedConv?.contactName || null;
    return selectedConv.contactName || contactNames.get(String(selectedConv.contactPhone || '').replace(/\D/g, '').slice(-10)) || null;
  }, [contactNames, selectedConv]);

  const [whatsappUnavailableUntilMs, setWhatsappUnavailableUntilMs] = useState<number | null>(null);
  useEffect(() => {
    const phone10 = selectedConv?.channel === 'whatsapp'
      ? String(selectedConv.contactPhone || '').replace(/\D/g, '').slice(-10)
      : '';
    if (phone10.length !== 10) {
      setWhatsappUnavailableUntilMs(null);
      return;
    }
    return onSnapshot(doc(db, 'whatsappContactState', phone10), (snapshot) => {
      const untilMs = snapshot.data()?.whatsappUnavailableUntil?.toMillis?.() || 0;
      setWhatsappUnavailableUntilMs(untilMs > Date.now() ? untilMs : null);
    }, () => setWhatsappUnavailableUntilMs(null));
  }, [selectedConv?.channel, selectedConv?.contactPhone]);

  const handleOpenBroker = (brokerId: string) => {
    const trimmedBrokerId = String(brokerId || '').trim();
    if (!trimmedBrokerId) return;
    navigate(`/brokers?brokerId=${encodeURIComponent(trimmedBrokerId)}`);
  };

  const handleOpenCustomerUnit = (unitId?: string | null) => {
    const trimmedUnitId = String(unitId || '').trim();
    if (!isAdmin || !trimmedUnitId) return;
    window.open(`/office/units/${encodeURIComponent(trimmedUnitId)}`, '_blank', 'noopener,noreferrer');
  };

  const handleOpenLead = async (leadId: string) => {
    const trimmedLeadId = String(leadId || '').trim();
    if (!trimmedLeadId) return;
    try {
      const leadSnap = await getDoc(doc(db, 'leads', trimmedLeadId));
      if (!leadSnap.exists()) return;
      const leadData = leadSnap.data() as Record<string, unknown>;
      setDrawerLead({
        id: leadSnap.id,
        name: String(leadData.name || '').trim(),
        status: String(leadData.status || '').trim(),
        ...leadData,
      });
      setLeadDrawerOpen(true);
    } catch (err) {
      console.error('Failed to open lead from messages', err);
    }
  };

  const showList = !isMobile || !selectedConv;
  const showChat = !isMobile || !!selectedConv;

  return (
    <Box sx={{ fontFamily: FONT_BODY, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        sx={{ px: 3, py: 2, borderBottom: `1px solid ${BORDER}` }}
      >
        <ChatBubbleOutlineIcon sx={{ color: GOLD, fontSize: 22 }} />
        <Typography sx={{ fontFamily: FONT_HEADING, fontSize: 18, fontWeight: 500 }}>
          Messages
        </Typography>
        {isAdmin && (
          <Tooltip title="WhatsApp settings">
            <IconButton size="small" onClick={() => navigate('/whatsapp?tab=settings')} sx={{ color: MUTED, ml: -0.5 }}>
              <SettingsOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" gap={0.5}>
          <Chip
            label="All"
            size="small"
            variant={channelFilter === 'all' ? 'filled' : 'outlined'}
            onClick={() => setChannelFilter('all')}
            sx={{
              fontSize: 12,
              ...(channelFilter === 'all' && { bgcolor: GOLD, color: '#fff' }),
            }}
          />
          <Chip
            icon={<WhatsAppIcon sx={{ fontSize: 14 }} />}
            label="WhatsApp"
            size="small"
            variant={channelFilter === 'whatsapp' ? 'filled' : 'outlined'}
            onClick={() => setChannelFilter('whatsapp')}
            sx={{
              fontSize: 12,
              ...(channelFilter === 'whatsapp' && { bgcolor: '#25d366', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }),
            }}
          />
          <Tooltip title="Coming soon">
            <span>
              <Chip
                icon={<InstagramIcon sx={{ fontSize: 14 }} />}
                label="Instagram"
                size="small"
                variant="outlined"
                disabled
                sx={{ fontSize: 12 }}
              />
            </span>
          </Tooltip>
          <Tooltip title="Coming soon">
            <span>
              <Chip
                icon={<FacebookIcon sx={{ fontSize: 14 }} />}
                label="Facebook"
                size="small"
                variant="outlined"
                disabled
                sx={{ fontSize: 12 }}
              />
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Box
        sx={{
          px: 3,
          py: 1.25,
          borderBottom: `1px solid ${BORDER}`,
          bgcolor: '#fffdf9',
          overflowX: { xs: 'auto', sm: 'visible' },
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
        }}
      >
        <Stack
          direction="row"
          gap={1}
          flexWrap={{ xs: 'nowrap', sm: 'wrap' }}
          sx={{
            minWidth: 'max-content',
            pb: { xs: 0.25, sm: 0 },
          }}
        >
          {[
            { key: 'all', label: 'All' },
            { key: 'my_leads', label: 'My Leads' },
            { key: 'customer', label: 'Customer' },
            { key: 'broker', label: 'Broker' },
            { key: 'unlinked', label: 'Unlinked' },
            { key: 'new_status', label: 'New Status' },
            { key: 'unread', label: 'Unread' },
            { key: 'not_lead', label: 'Not a lead' },
          ].filter((option) => seeAllChats || option.key === 'my_leads' || option.key === 'unread').map((option) => {
            const isActive = leadFilter === option.key;
            const unreadCount =
              leadFilterCounts[option.key as ConversationLeadFilter] || 0;
            return (
              <Badge
                key={option.key}
                badgeContent={unreadCount > 0 ? unreadCount : 0}
                color="warning"
                overlap="rectangular"
                invisible={unreadCount <= 0}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{
                  '& .MuiBadge-badge': {
                    minWidth: 18,
                    height: 18,
                    px: 0.5,
                    fontSize: 10,
                    fontWeight: 700,
                    bgcolor: GOLD,
                    color: '#fff',
                  },
                }}
              >
                <Chip
                  label={option.label}
                  size="small"
                  variant={isActive ? 'filled' : 'outlined'}
                  onClick={() =>
                    setLeadFilter(option.key as ConversationLeadFilter)
                  }
                  sx={{
                    fontSize: 12,
                    fontWeight: isActive ? 600 : 500,
                    ...(isActive && {
                      bgcolor: 'rgba(157,111,50,0.14)',
                      color: GOLD,
                      borderColor: 'rgba(157,111,50,0.28)',
                    }),
                  }}
                />
              </Badge>
            );
          })}
        </Stack>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {showList && (
          <Box
            sx={{
              width: isMobile ? '100%' : 320,
              borderRight: isMobile ? 'none' : `1px solid ${BORDER}`,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
              <TextField
                placeholder="Search by name or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon sx={{ fontSize: 18, color: MUTED }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: 13,
                    borderRadius: '8px',
                    bgcolor: SIDEBAR_BG,
                  },
                }}
              />
            </Box>

            <List sx={{ flex: 1, overflowY: 'auto', py: 0 }}>
              {loading ? (
                <Box sx={{ textAlign: 'center', pt: 4, color: MUTED }}>
                  <Typography sx={{ fontSize: 13 }}>Loading conversations...</Typography>
                </Box>
              ) : filtered.length === 0 ? (
                <Box sx={{ textAlign: 'center', pt: 4, color: MUTED }}>
                  <Typography sx={{ fontSize: 13 }}>No conversations found</Typography>
                </Box>
              ) : (
                filtered.map((conv) => {
                  const ci = hashCode(conv.contactId) % AVATAR_COLORS.length;
                  const isActive = selectedConv?.contactId === conv.contactId;
                  const lastMsg = conv.lastMessage;
                  const customerTooltip = conv.customerUnitId ? (
                    <Box sx={{ p: 0.25 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3 }}>
                        {conv.customerName || conv.contactDisplay || 'Customer'}
                      </Typography>
                      {(conv.customerLinkedUnits || []).map((unit) => (
                        <Typography key={`${unit.projectId}/${unit.unitId}`} sx={{ fontSize: 11, lineHeight: 1.3 }}>
                          {unit.projectName} · Unit {unit.unitNumber}
                        </Typography>
                      ))}
                    </Box>
                  ) : null;
                  let msgText = lastMsg.type === 'template'
                    ? getMessageDisplayText(lastMsg, templateEdits)
                    : lastMsg.text;
                  if (!msgText) {
                    const typeLabels: Record<string, string> = {
                      image: '📷 Photo',
                      video: '🎥 Video',
                      document: '📄 Document',
                      audio: '🎵 Audio',
                      sticker: '🏷️ Sticker',
                      location: '📍 Location',
                      contacts: '👤 Contact',
                    };
                    if (lastMsg.type === 'template') {
                      const comps = (lastMsg as any).templateComponents;
                      if (Array.isArray(comps)) {
                        const body = comps.find((c: any) => c.type === 'body');
                        if (body?.parameters?.length) {
                          msgText = body.parameters.map((p: any) => p.text).filter(Boolean).join(', ');
                        }
                      }
                      if (!msgText) msgText = lastMsg.templateName ? `📋 ${getTemplateDisplayName(lastMsg.templateName)}` : '[template]';
                    } else if (typeLabels[lastMsg.type]) {
                      msgText = typeLabels[lastMsg.type];
                    } else if (lastMsg.type === 'unsupported') {
                      msgText = formatUnsupportedMessageLabel(lastMsg);
                    }
                  }
                  const preview =
                    (lastMsg.direction === 'outbound' ? 'You: ' : '') +
                    (msgText || `[${lastMsg.type}]`);

                  return (
                    <ListItemButton
                      key={conv.contactId}
                      selected={isActive}
                      onClick={() => handleSelect(conv)}
                      sx={{
                        px: 2,
                        py: 1.25,
                        gap: 1.25,
                        borderBottom: `1px solid ${BORDER}`,
                        '&.Mui-selected': { bgcolor: 'rgba(157,111,50,0.08)' },
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
                      }}
                    >
                      <Box sx={{ position: 'relative', flexShrink: 0 }}>
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: '50%',
                            bgcolor: AVATAR_COLORS[ci],
                            color: AVATAR_TEXT[ci],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 500,
                            fontSize: 14,
                          }}
                        >
                          {getInitials(conv.contactName)}
                        </Box>
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: -2,
                            right: -2,
                            bgcolor: '#fff',
                            borderRadius: '50%',
                            p: '1px',
                            lineHeight: 0,
                          }}
                        >
                          {channelIcon(conv.channel, 13)}
                        </Box>
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" gap={0.75}>
                          <Typography
                            sx={{
                              fontSize: 13.5,
                              fontWeight: conv.unreadCount > 0 ? 600 : 500,
                              lineHeight: 1.3,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {conv.contactDisplay}
                          </Typography>
                          {conv.brokerId && (
                            <Chip
                              label="Broker"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: 10,
                                bgcolor: 'rgba(47, 128, 237, 0.10)',
                                color: '#2F80ED',
                                fontWeight: 600,
                                maxWidth: 80,
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          )}
                          {conv.customerUnitId && (
                            <Tooltip title={customerTooltip}>
                              <Chip
                              label={(conv.customerLinkedUnits?.length || 0) > 1
                                ? `Customer · ${conv.customerLinkedUnits?.length} units`
                                : 'Customer'}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: 10,
                                  bgcolor: 'rgba(14, 165, 233, 0.10)',
                                  color: '#0284C7',
                                  fontWeight: 600,
                                  maxWidth: 145,
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            </Tooltip>
                          )}
                          {conv.leadName && (
                            <Chip
                              label={conv.leadName}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: 10,
                                bgcolor: 'rgba(157,111,50,0.1)',
                                color: GOLD,
                                fontWeight: 500,
                                maxWidth: 80,
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          )}
                        </Stack>
                        <Typography sx={{ fontSize: 11, color: MUTED, lineHeight: 1.2 }}>
                          {formatPhoneDisplay(conv.contactPhone || '')}
                        </Typography>
                        {(() => {
                          const request = openRequests.get(String(conv.contactPhone || '').replace(/\D/g, '').slice(-10));
                          return request ? (
                            <Chip
                              size="small"
                              label={`${request.type === 'visit' ? '📍' : '📞'} ${request.label}`}
                              title="Requested on WhatsApp. Clears when someone replies."
                              sx={{
                                mt: 0.4,
                                height: 18,
                                fontSize: 10.5,
                                fontWeight: 600,
                                bgcolor: '#fbf3e6',
                                color: '#8a5a12',
                                maxWidth: '100%',
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          ) : null;
                        })()}
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: conv.unreadCount > 0 ? DARK : MUTED,
                            fontWeight: conv.unreadCount > 0 ? 500 : 400,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            mt: 0.25,
                          }}
                        >
                          {preview}
                        </Typography>
                      </Box>

                      <Stack alignItems="flex-end" gap={0.5} sx={{ flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 11, color: MUTED }}>
                          {formatMessageTimestamp(conv.lastMessage.createdAt)}
                        </Typography>
                        {conv.unreadCount > 0 && (
                          <Box
                            sx={{
                              minWidth: 18,
                              height: 18,
                              borderRadius: '50%',
                              bgcolor: GOLD,
                              color: '#fff',
                              fontSize: 10,
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              px: 0.5,
                            }}
                          >
                            {conv.unreadCount}
                          </Box>
                        )}
                      </Stack>
                    </ListItemButton>
                  );
                })
              )}
              {!loading && filtered.length > 0 && hasMoreMessages && (
                <Box sx={{ py: 1.5, px: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={loadOlderMessages}
                    disabled={loadingMoreMessages}
                    sx={{
                      borderColor: 'rgba(157,111,50,0.28)',
                      color: GOLD,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: GOLD,
                        bgcolor: 'rgba(157,111,50,0.06)',
                      },
                    }}
                  >
                    {loadingMoreMessages ? 'Loading older messages...' : 'Load older messages'}
                  </Button>
                </Box>
              )}
            </List>
          </Box>
        )}

        {showChat && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {selectedConv ? (
              <>
              {whatsappUnavailableUntilMs && whatsappUnavailableUntilMs > Date.now() && (
                <Alert severity="warning" sx={{ borderRadius: 0, py: 0 }}>
                  WhatsApp temporarily unavailable for this number until{' '}
                  {new Date(whatsappUnavailableUntilMs).toLocaleDateString('en-IN', {
                    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata',
                  })}. Booking messages will use an available secondary applicant.
                </Alert>
              )}
              <ChannelChat
                channel={selectedConv.channel}
                contactId={selectedConv.contactId}
                contactName={selectedContactName}
                leadId={selectedConv.leadId}
                brokerId={selectedConv.brokerId}
                customerUnitId={selectedConv.customerUnitId || undefined}
                customerUnitNumber={selectedConv.customerUnitNumber || undefined}
                customerProjectName={selectedConv.customerProjectName || undefined}
                customerName={selectedConv.customerName || undefined}
                customerLinkedUnits={selectedConv.customerLinkedUnits}
                projectId={undefined}
                onBack={isMobile ? () => {
                  const next = new URLSearchParams(searchParams);
                  next.delete('chat');
                  setSearchParams(next);
                } : undefined}
                hasLead={!!selectedConv.leadId}
                hasBroker={!!selectedConv.brokerId}
                hasCustomer={!!selectedConv.customerUnitId}
                canOpenCustomerUnit={isAdmin && !!selectedConv.customerUnitId}
                onNameUpdated={(phone10, name) => {
                  setContactNames((prev) => {
                    const next = new Map(prev);
                    next.set(phone10, name);
                    return next;
                  });
                  setSelectedConv((prev) => {
                    if (!prev) return prev;
                    const prevPhone10 = String(prev.contactPhone || '').replace(/\D/g, '').slice(-10);
                    if (prevPhone10 !== phone10) return prev;
                    return {
                      ...prev,
                      contactName: name,
                      contactDisplay: name,
                    };
                  });
                }}
                onAddLead={(phone, name) => {
                  setAddLeadPrefill({ phone, name, source: 'WhatsApp' });
                  setAddLeadOpen(true);
                }}
                onOpenLead={handleOpenLead}
                onOpenBroker={handleOpenBroker}
                onOpenCustomerUnit={handleOpenCustomerUnit}
              />
              </>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: MUTED,
                  gap: 1,
                }}
              >
                <ChatBubbleOutlineIcon sx={{ fontSize: 48, opacity: 0.25 }} />
                <Typography sx={{ fontSize: 14 }}>
                  Select a conversation to start messaging
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
      <AddLeadDrawer
        open={addLeadOpen}
        onClose={() => { setAddLeadOpen(false); setAddLeadPrefill(null); }}
        onCreated={() => {
          getDocs(collection(db, 'leads')).then((snap) => {
            const m = new Map<string, LeadMapEntry>();
            snap.docs.forEach((d) => {
              const data = d.data();
              const phone = String(data.phone || '').replace(/\D/g, '');
              const phone10 = String(data.phone10 || '').trim();
              const name = String(data.name || '').trim();
              const entry: LeadMapEntry = {
                id: d.id,
                name,
                assignedTo: String(data.assignedTo || '').trim() || null,
                status: String(data.status || '').trim() || null,
              };
              if (phone10) m.set(phone10, entry);
              if (phone.length >= 10) m.set(phone.slice(-10), entry);
            });
            setLeadMap(m);
          });
        }}
        prefill={addLeadPrefill || undefined}
      />
      <LeadDetailDrawer
        lead={drawerLead as any}
        open={leadDrawerOpen}
        closeOnOutsideClick
        onClose={() => {
          setLeadDrawerOpen(false);
          setDrawerLead(null);
        }}
        onUpdated={(nextLead) => setDrawerLead(nextLead as any)}
        onDeleted={(deletedLeadId) => {
          setLeadDrawerOpen(false);
          setDrawerLead((prev) =>
            prev && prev.id === deletedLeadId ? null : prev
          );
        }}
      />
    </Box>
  );
}
